import React, { useState, useEffect, useMemo } from 'react';
import { getReceita, getReceitas, createReceita, updateReceita, deleteReceita, createInsumo, entradaInsumo, getInsumos, updateInsumo, deleteInsumo } from '../api';
import InsumosPage from './InsumosPage';

const TIPOS = [
  { value: 'final',          label: '🍱 Marmita / Prato final' },
  { value: 'base',           label: '🍚 Base (arroz, feijão, macarrão…)' },
  { value: 'acompanhamento', label: '🥦 Acompanhamento / Salada' },
  { value: 'molho',          label: '🫙 Molho / Caldo / Tempero' },
];

// compatibilidade com valores legados do banco
const TIPO_KEY_MAP = { cobertura: 'molho', recheio: 'acompanhamento' };

function tipoLabel(tipo) {
  const t = TIPO_KEY_MAP[tipo] || tipo;
  return TIPOS.find(x => x.value === t)?.label || tipo;
}

function fmt(v)    { return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }); }
function fmtPct(v) { return `${Number(v || 0).toFixed(1)}%`; }

/* ── Breakdown de custo ─────────────────────────────────────────────────── */
function CustoBreakdown({ custo }) {
  if (!custo) return null;
  const rows = [
    { label: '🥛 Insumos',        val: custo.custoInsumos,   color: 'var(--y)' },
    { label: '👩‍🍳 Mão de obra',   val: custo.custoMaoObra,   color: 'var(--b)' },
    { label: '🏠 Despesas fixas', val: custo.custoDespFixas, color: 'var(--c4)' },
    { label: '🔥 Variáveis',      val: custo.custoVariaveis, color: 'var(--c3)' },
  ];
  const pctMarkup = custo.precoSugerido > 0
    ? ((custo.precoSugerido - custo.custoTotal) / custo.precoSugerido) * 100
    : 0;
  return (
    <div style={{ background: 'var(--g4)', borderRadius: 12, padding: '14px 16px', marginTop: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--t2)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>
        Composição do custo
      </div>
      {rows.map(r => (
        <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
          <span style={{ color: 'var(--t2)' }}>{r.label}</span>
          <strong style={{ color: r.color }}>{fmt(r.val)}</strong>
        </div>
      ))}
      <div style={{ borderTop: '1px solid var(--c3)', marginTop: 8, paddingTop: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700, color: 'var(--t)' }}>
          <span>Custo total</span><strong>{fmt(custo.custoTotal)}</strong>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--t2)', marginTop: 4 }}>
          <span>Por unidade (rende {custo.rendimento})</span>
          <strong>{fmt(custo.custoPorUnidade)}</strong>
        </div>
        {custo.tempoTotal > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--t2)', marginTop: 4 }}>
            <span>Tempo total de preparo</span><strong>{custo.tempoTotal} min</strong>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--t2)', marginTop: 6 }}>
          <span>+ Markup ({fmtPct(pctMarkup)})</span>
          <strong>{fmt(custo.markup)}</strong>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 800, marginTop: 8, color: 'var(--g)', borderTop: '1px solid var(--c3)', paddingTop: 8 }}>
          <span>Preço sugerido</span><strong>{fmt(custo.precoSugerido)}</strong>
        </div>
        {custo.rendimento > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--t2)', marginTop: 2 }}>
            <span>Por unidade</span><strong>{fmt(custo.precoSugerido / custo.rendimento)}</strong>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Linha de ingrediente ───────────────────────────────────────────────── */
function IngredienteRow({ item, insumos, subreceitas, onChange, onRemove }) {
  const insumoSel = item.insumo_id ? insumos.find(i => i.id === item.insumo_id) : null;
  const custoEst  = insumoSel && item.quantidade
    ? Number(insumoSel.custo_unitario) * Number(item.quantidade)
    : null;

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginBottom: 8, background: 'var(--g4)', borderRadius: 10, padding: '8px 10px' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <select
          className="inp inp-sel"
          style={{ marginBottom: 6, width: '100%' }}
          value={item.insumo_id ? `i-${item.insumo_id}` : `r-${item.subreceita_id}`}
          onChange={e => {
            const v = e.target.value;
            if (v.startsWith('i-')) onChange({ ...item, insumo_id: Number(v.slice(2)), subreceita_id: null });
            else onChange({ ...item, subreceita_id: Number(v.slice(2)), insumo_id: null });
          }}
        >
          <optgroup label="Ingredientes">
            {insumos.map(i => (
              <option key={`i-${i.id}`} value={`i-${i.id}`}>
                {i.nome}  —  R$ {Number(i.custo_unitario).toFixed(4)}/{i.unidade}
              </option>
            ))}
          </optgroup>
          {subreceitas.length > 0 && (
            <optgroup label="Preparos base (sub-receitas)">
              {subreceitas.map(r => <option key={`r-${r.id}`} value={`r-${r.id}`}>{r.nome}</option>)}
            </optgroup>
          )}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            className="inp"
            type="number"
            step="0.001"
            min="0.001"
            placeholder={`Quantidade${insumoSel ? ` em ${insumoSel.unidade}` : ''}`}
            value={item.quantidade}
            onChange={e => onChange({ ...item, quantidade: e.target.value })}
            style={{ flex: 1 }}
          />
          {insumoSel && (
            <span style={{ fontSize: 12, color: 'var(--c4)', flexShrink: 0, minWidth: 24 }}>
              {insumoSel.unidade}
            </span>
          )}
        </div>
        {custoEst !== null && (
          <div style={{ fontSize: 11, color: 'var(--y)', marginTop: 4 }}>
            ≈ {fmt(custoEst)} neste ingrediente
          </div>
        )}
      </div>
      <button
        type="button"
        style={{ background: 'var(--r3)', color: 'var(--r)', border: '1px solid #fecaca', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', flexShrink: 0 }}
        onClick={onRemove}
      >✕</button>
    </div>
  );
}

/* ── Modal rápido de novo ingrediente ───────────────────────────────────── */
function NovoIngredienteModal({ onSalvo, onFechar }) {
  const [nome,     setNome]     = useState('');
  const [unidade,  setUnidade]  = useState('g');
  const [peso,     setPeso]     = useState('');
  const [preco,    setPreco]    = useState('');
  const [saving,   setSaving]   = useState(false);
  const [erro,     setErro]     = useState('');

  const custoUnit = peso && preco && Number(peso) > 0
    ? (Number(preco) / Number(peso)).toFixed(6)
    : null;

  const handleSubmit = async e => {
    e.preventDefault();
    if (!nome.trim()) return setErro('Informe o nome do ingrediente');
    if (!peso || !preco) return setErro('Informe o preço e o peso da embalagem');
    setSaving(true);
    setErro('');
    try {
      const ins = await createInsumo({ nome: nome.trim(), unidade, peso_embalagem: Number(peso) });
      await entradaInsumo(ins.id, {
        quantidade:  Number(peso),
        preco_pago:  Number(preco),
        peso_lote:   Number(peso),
        observacao:  'Cadastro inicial',
      });
      const atualizado = await getInsumos();
      onSalvo(atualizado, ins.id);
    } catch (err) {
      setErro(err.message);
    }
    setSaving(false);
  };

  return (
    <div className="ov" onClick={onFechar}>
      <div className="mo" onClick={e => e.stopPropagation()}>
        <div className="mo-handle" />
        <h3>🧂 Novo ingrediente</h3>
        <div className="mo-sub">Cadastra o ingrediente e o preço pago para calcular o custo</div>
        <form className="fm" onSubmit={handleSubmit}>
          {erro && <div className="err">{erro}</div>}

          <input
            className="inp"
            placeholder="Nome do ingrediente  (ex: Frango peito) *"
            value={nome}
            onChange={e => setNome(e.target.value)}
            required autoFocus
          />

          <div style={{ fontSize: 12, color: 'var(--c4)', marginBottom: 4, marginTop: 4 }}>
            Qual a unidade de medida deste ingrediente?
          </div>
          <select className="inp inp-sel" value={unidade} onChange={e => setUnidade(e.target.value)}>
            <option value="g">g — gramas (carnes, queijos, farinhas…)</option>
            <option value="ml">ml — mililitros (óleos, molhos, leite…)</option>
            <option value="un">un — unidade (ovos, sachês…)</option>
          </select>

          <div style={{ fontSize: 12, color: 'var(--c4)', marginBottom: 4, marginTop: 12 }}>
            Quanto você paga pela embalagem?
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: 'var(--c4)', display: 'block', marginBottom: 3 }}>
                Peso da embalagem ({unidade})
              </label>
              <input
                className="inp"
                type="number" step="0.001" min="0.001"
                placeholder={unidade === 'g' ? 'Ex: 1000' : unidade === 'ml' ? 'Ex: 900' : 'Ex: 12'}
                value={peso}
                onChange={e => setPeso(e.target.value)}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: 'var(--c4)', display: 'block', marginBottom: 3 }}>
                Preço pago (R$)
              </label>
              <input
                className="inp"
                type="number" step="0.01" min="0.01"
                placeholder="Ex: 12,90"
                value={preco}
                onChange={e => setPreco(e.target.value)}
              />
            </div>
          </div>

          {custoUnit && (
            <div style={{ padding: '8px 12px', background: 'var(--g4)', borderRadius: 10, fontSize: 13, marginTop: 8 }}>
              Custo por {unidade}: <strong style={{ color: 'var(--g)' }}>R$ {custoUnit}</strong>
            </div>
          )}

          <div className="btn-g" style={{ marginTop: 16 }}>
            <button className="btn btn1" type="submit" disabled={saving}>
              {saving ? 'Salvando...' : '✅ Cadastrar ingrediente'}
            </button>
            <button className="btn btn3" type="button" onClick={onFechar}>Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Formulário de receita ──────────────────────────────────────────────── */
function ReceitaForm({ initialData, insumos: insumosProp, subreceitas, produtos, onSubmit, onCancel, saving }) {
  const [insumos,     setInsumos]     = useState(insumosProp || []);
  const [showNovoIng, setShowNovoIng] = useState(false);
  // Carrega lista própria de insumos — não sincroniza com prop para evitar re-render do pai
  useEffect(() => {
    if (insumosProp?.length && !insumos.length) setInsumos(insumosProp);
  }, []);
  const [nome,        setNome]        = useState(initialData?.nome || '');
  const [tipo,        setTipo]        = useState(TIPO_KEY_MAP[initialData?.tipo] || initialData?.tipo || 'final');
  const [rendimento,  setRendimento]  = useState(initialData?.rendimento || 1);
  const [rendUnidade, setRendUnidade] = useState(initialData?.rendimento_unidade || 'un');
  const [tempo,       setTempo]       = useState(initialData?.tempo_preparo_min || 0);
  const [produtoId,   setProdutoId]   = useState(initialData?.produto_id ? String(initialData.produto_id) : '');
  const [markup,      setMarkup]      = useState(initialData?.percentual_markup != null ? String(initialData.percentual_markup) : '');
  const [itens,       setItens]       = useState(
    initialData?.itens?.map(i => ({
      insumo_id:     i.insumo_id || null,
      subreceita_id: i.subreceita_id || null,
      quantidade:    String(i.quantidade),
    })) || []
  );

  const custoEstimado = useMemo(() => {
    let total = 0;
    itens.forEach(it => {
      if (!it.insumo_id || !it.quantidade) return;
      const ins = insumos.find(i => i.id === it.insumo_id);
      if (ins) total += Number(ins.custo_unitario) * Number(it.quantidade);
    });
    return total;
  }, [itens, insumos]);

  const addItem = () => {
    if (!insumos.length) return;
    setItens(prev => [...prev, { insumo_id: insumos[0].id, subreceita_id: null, quantidade: '' }]);
  };

  const handleSubmit = e => {
    e.preventDefault();
    onSubmit({
      nome: nome.trim(),
      tipo,
      rendimento: Number(rendimento),
      rendimento_unidade: rendUnidade,
      tempo_preparo_min: Number(tempo),
      produto_id: produtoId ? Number(produtoId) : null,
      percentual_markup: markup !== '' ? Number(markup) : null,
      itens: itens
        .filter(i => (i.insumo_id || i.subreceita_id) && i.quantidade)
        .map(i => ({ insumo_id: i.insumo_id, subreceita_id: i.subreceita_id, quantidade: Number(i.quantidade) })),
    });
  };

  const S = { label: { fontSize: 12, color: 'var(--c4)', display: 'block', marginBottom: 3 } };

  return (
  <>
    <form onSubmit={handleSubmit}>

      {/* Bloco 1 — Identificação */}
      <div style={{ fontWeight: 700, fontSize: 11, color: 'var(--c4)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
        1. Identificação
      </div>
      <input
        className="inp"
        placeholder="Nome  (ex: Marmita Frango Grelhado com Arroz e Salada) *"
        value={nome}
        onChange={e => setNome(e.target.value)}
        required
        autoFocus
        style={{ marginBottom: 8 }}
      />
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <select className="inp inp-sel" value={tipo} onChange={e => setTipo(e.target.value)} style={{ flex: 1 }}>
          {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        {tipo === 'final' && (
          <select className="inp inp-sel" value={produtoId} onChange={e => setProdutoId(e.target.value)} style={{ flex: 1 }}>
            <option value="">Sem produto vinculado</option>
            {produtos.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
          </select>
        )}
      </div>

      {/* Bloco 2 — Produção */}
      <div style={{ fontWeight: 700, fontSize: 11, color: 'var(--c4)', textTransform: 'uppercase', letterSpacing: 1, margin: '16px 0 8px' }}>
        2. Produção (para calcular custo por unidade)
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <label style={S.label}>Quantas marmitas essa receita faz?</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input className="inp" type="number" min="1" step="1" placeholder="Ex: 4" value={rendimento} onChange={e => setRendimento(e.target.value)} />
            <select className="inp inp-sel" value={rendUnidade} onChange={e => setRendUnidade(e.target.value)} style={{ maxWidth: 70 }}>
              {['un', 'g', 'ml'].map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <label style={S.label}>Tempo de preparo</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input className="inp" type="number" min="0" placeholder="Ex: 45" value={tempo} onChange={e => setTempo(e.target.value)} />
            <span style={{ fontSize: 12, color: 'var(--c4)', flexShrink: 0 }}>min</span>
          </div>
        </div>
      </div>

      {/* Markup por receita */}
      <div style={{ marginBottom: 8 }}>
        <label style={S.label}>
          Markup desta receita  <span style={{ fontWeight: 400, color: 'var(--c4)' }}>(deixe vazio para usar o padrão global)</span>
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, maxWidth: 180 }}>
          <input
            className="inp"
            type="number" min="0" max="999" step="0.1"
            placeholder="Ex: 70"
            value={markup}
            onChange={e => setMarkup(e.target.value)}
          />
          <span style={{ fontSize: 13, color: 'var(--c4)', flexShrink: 0 }}>%</span>
        </div>
      </div>

      {/* Bloco 3 — Ingredientes */}
      <div style={{ fontWeight: 700, fontSize: 11, color: 'var(--c4)', textTransform: 'uppercase', letterSpacing: 1, margin: '16px 0 8px' }}>
        3. Ingredientes
      </div>

      {itens.length === 0 && insumos.length === 0 && (
        <div style={{ padding: '12px 14px', background: 'var(--y3,#fefce8)', border: '1px solid var(--y,#ca8a04)', borderRadius: 12, fontSize: 13, color: 'var(--c4)', marginBottom: 12, lineHeight: 1.6 }}>
          Nenhum ingrediente cadastrado ainda. Clique em <strong>"+ Novo ingrediente"</strong> abaixo para adicionar o primeiro.
        </div>
      )}

      {itens.map((item, idx) => (
        <IngredienteRow
          key={idx}
          item={item}
          insumos={insumos}
          subreceitas={subreceitas}
          onChange={v => setItens(prev => prev.map((x, i) => i === idx ? v : x))}
          onRemove={() => setItens(prev => prev.filter((_, i) => i !== idx))}
        />
      ))}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {insumos.length > 0 && (
          <button type="button" className="btn btn3 btn-sm" onClick={addItem}>
            + Adicionar à ficha
          </button>
        )}
        <button type="button" className="btn btn3 btn-sm" onClick={() => setShowNovoIng(true)}>
          🧂 Novo ingrediente
        </button>
      </div>

      {custoEstimado > 0 && (
        <div style={{ padding: '10px 14px', background: 'var(--g4)', borderRadius: 10, fontSize: 13, marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
          <span>Custo estimado dos ingredientes</span>
          <span>
            <strong style={{ color: 'var(--y)' }}>{fmt(custoEstimado)}</strong>
            {Number(rendimento) > 1 && (
              <span style={{ color: 'var(--c4)', marginLeft: 8, fontSize: 12 }}>
                ({fmt(custoEstimado / Number(rendimento))}/un)
              </span>
            )}
          </span>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button className="btn btn1" type="submit" disabled={saving || !nome.trim()}>
          {saving ? 'Salvando...' : (initialData ? '💾 Salvar alterações' : '✅ Criar ficha técnica')}
        </button>
        <button className="btn btn3" type="button" onClick={onCancel}>Cancelar</button>
      </div>
    </form>

    {showNovoIng && (
      <NovoIngredienteModal
        onFechar={() => setShowNovoIng(false)}
        onSalvo={(novosInsumos, novoId) => {
          setInsumos(novosInsumos);
          setShowNovoIng(false);
          setItens(prev => [...prev, { insumo_id: novoId, subreceita_id: null, quantidade: '' }]);
        }}
      />
    )}
  </>
  );
}

/* ── Card de receita ────────────────────────────────────────────────────── */
function ReceitaCard({ receita, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const custo = receita.custo;

  return (
    <div className="c" style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 14 }}>{receita.nome}</strong>
            <span className="bd bd-g" style={{ fontSize: 10 }}>{tipoLabel(receita.tipo)}</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--c4)', marginTop: 2 }}>
            Rende: {receita.rendimento} {receita.rendimento_unidade}
            {receita.tempo_preparo_min > 0 && ` · ${receita.tempo_preparo_min} min`}
            {receita.produto_nome && ` · 📦 ${receita.produto_nome}`}
          </div>
          {custo ? (
            <div style={{ marginTop: 6, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 13 }}>Custo/un: <strong style={{ color: 'var(--y)' }}>{fmt(custo.custoPorUnidade)}</strong></span>
              <span style={{ fontSize: 13 }}>Preço sugerido: <strong style={{ color: 'var(--g)' }}>{fmt(custo.precoSugerido)}</strong></span>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--c4)', marginTop: 4 }}>
              Adicione ingredientes para ver o custo calculado
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {custo && (
            <button className="btn btn3 btn-sm" onClick={() => setExpanded(!expanded)} title="Ver custo detalhado">
              {expanded ? '▲' : '▼'}
            </button>
          )}
          <button className="btn btn3 btn-sm" onClick={() => onEdit(receita)}>✎</button>
          <button
            className="btn btn-sm"
            style={{ background: 'var(--r3)', color: 'var(--r)', border: '1px solid #fecaca' }}
            onClick={() => onDelete(receita)}
          >✕</button>
        </div>
      </div>
      {expanded && <CustoBreakdown custo={custo} />}
    </div>
  );
}

/* ── Sub-página: lista de fichas ────────────────────────────────────────── */
function TutorialFichas() {
  const [aberto, setAberto] = useState(false);
  return (
    <div style={{ marginBottom: 12, border: '1px solid var(--c3)', borderRadius: 12, overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setAberto(v => !v)}
        style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--g4)', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--t2)' }}
      >
        <span>💡 Como funciona a ficha técnica?</span>
        <span style={{ fontSize: 16, lineHeight: 1 }}>{aberto ? '▲' : '▼'}</span>
      </button>
      {aberto && (
        <div style={{ padding: '14px 16px', fontSize: 13, color: 'var(--t2)', lineHeight: 1.7, background: 'var(--w,#fff)' }}>

          <p style={{ margin: '0 0 12px' }}>
            A <strong>ficha técnica</strong> é a receita com custo. Ela lista os ingredientes e quanto de cada um você usa, e calcula automaticamente o custo de produção e o preço sugerido de venda.
          </p>

          <div style={{ fontWeight: 700, color: 'var(--t)', marginBottom: 6 }}>Como o custo é calculado?</div>
          <div style={{ background: 'var(--g4)', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
            <div style={{ marginBottom: 4 }}>🥛 <strong>Insumos</strong> — custo dos ingredientes usados (quantidade × preço por grama/ml/un)</div>
            <div style={{ marginBottom: 4 }}>👩‍🍳 <strong>Mão de obra</strong> — custo do seu tempo (pró-labore ÷ minutos produtivos × tempo de preparo)</div>
            <div style={{ marginBottom: 4 }}>🏠 <strong>Despesas fixas</strong> — aluguel, luz, contador etc. rateados pelo tempo de preparo</div>
            <div>🔥 <strong>Variáveis</strong> — gás, limpeza, perdas (% configurável sobre o custo dos ingredientes)</div>
          </div>

          <div style={{ fontWeight: 700, color: 'var(--t)', marginBottom: 6 }}>O que é markup?</div>
          <div style={{ background: 'var(--g4)', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
            <p style={{ margin: '0 0 8px' }}>
              Markup é o percentual que você <strong>soma ao custo</strong> para chegar ao preço de venda. Serve para cobrir o lucro desejado.
            </p>
            <div style={{ fontFamily: 'monospace', fontSize: 12, background: 'var(--c2)', borderRadius: 8, padding: '8px 12px', marginBottom: 8, color: 'var(--t)' }}>
              Custo total: R$ 10,00<br />
              Markup 70%: + R$ 7,00<br />
              <strong>Preço sugerido: R$ 17,00</strong>
            </div>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--t2)' }}>
              Markup <strong>não é</strong> margem de lucro. Com markup de 70%, sua margem real é ~41% — porque o percentual é calculado sobre o custo, não sobre o preço de venda. Configure o padrão em <strong>Financeiro → Precificação</strong>. Cada ficha pode ter seu próprio markup se precisar.
            </p>
          </div>

          <div style={{ fontWeight: 700, color: 'var(--t)', marginBottom: 6 }}>Dica: vincule ao produto</div>
          <p style={{ margin: 0 }}>
            Se você cadastrou um produto em <strong>Estoque</strong>, pode vincular a ficha a ele. Assim o sistema compara o custo calculado com o preço que você pratica e mostra a margem real.
          </p>

        </div>
      )}
    </div>
  );
}

function FichasPage({ insumos, setInsumos, receitas, setReceitas, produtos, onToast, onReload, initialTarget, onTargetConsumed }) {
  const [saving,          setSaving]          = useState(false);
  const [showForm,        setShowForm]        = useState(false);
  const [editando,        setEditando]        = useState(null);
  const [detalhes,        setDetalhes]        = useState(null);
  const [busca,           setBusca]           = useState('');
  const [filtroTipo,      setFiltroTipo]      = useState('todos');
  const [initialProduto,  setInitialProduto]  = useState(null);

  const load = async () => {
    try { const d = await getReceitas(); setReceitas(d); } catch {}
  };

  const doSave = async (fn, msg) => {
    setSaving(true);
    try { await fn(); (onToast || (() => {}))(msg); load(); if (onReload) onReload(); }
    catch (err) { (onToast || (() => {}))(err.message, 'error'); }
    setSaving(false);
  };

  const onCreate = d => doSave(() => createReceita(d), 'Ficha técnica criada!');
  const onEdit   = (id, d) => doSave(() => updateReceita(id, d), 'Ficha atualizada!');
  const onDelete = r => doSave(() => deleteReceita(r.id), 'Ficha removida');

  const subreceitas = useMemo(() => receitas.filter(r => r.tipo !== 'final'), [receitas]);

  const lista = useMemo(() =>
    receitas
      .filter(r => filtroTipo === 'todos' || r.tipo === filtroTipo)
      .filter(r => !busca.trim() || r.nome.toLowerCase().includes(busca.toLowerCase())),
    [receitas, filtroTipo, busca]
  );

  const abrirEdicao = async (r) => {
    try {
      const det = await getReceita(r.id);
      setDetalhes(det);
      setEditando(r);
      setShowForm(true);
    } catch (err) {
      (onToast || (() => {}))(err.message, 'error');
    }
  };

  const fecharForm = () => { setShowForm(false); setEditando(null); setDetalhes(null); setInitialProduto(null); };

  useEffect(() => {
    if (!initialTarget) return;
    if (initialTarget.receitaId) {
      const r = receitas.find(x => x.id === initialTarget.receitaId);
      if (r) abrirEdicao(r);
    } else if (initialTarget.produtoId) {
      setInitialProduto(initialTarget.produtoId);
      setEditando(null);
      setDetalhes(null);
      setShowForm(true);
    }
    if (onTargetConsumed) onTargetConsumed();
  }, [initialTarget]); // eslint-disable-line

  return (
    <div>
      <div className="c-h" style={{ marginBottom: 12 }}>
        <span className="c-t">Fichas Técnicas</span>
        {!showForm && (
          <button className="btn btn1 btn-sm" onClick={() => { setShowForm(true); setEditando(null); setDetalhes(null); }}>
            + Nova ficha
          </button>
        )}
      </div>

      {showForm && !editando && (
        <div className="c" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>📝 Nova ficha técnica</div>
          <ReceitaForm
            initialData={initialProduto ? { produto_id: initialProduto } : null}
            insumos={insumos}
            subreceitas={subreceitas}
            produtos={produtos}
            saving={saving}
            onSubmit={async d => { await onCreate(d); fecharForm(); }}
            onCancel={fecharForm}
          />
        </div>
      )}

      {!showForm && (
        <>
          <TutorialFichas />
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: 160 }}>
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>🔍</span>
              <input className="inp" style={{ paddingLeft: 36 }} placeholder="Buscar ficha..." value={busca} onChange={e => setBusca(e.target.value)} />
            </div>
            <select className="inp inp-sel" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)} style={{ maxWidth: 200 }}>
              <option value="todos">Todos os tipos</option>
              {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </>
      )}

      {lista.length === 0 && !showForm ? (
        <div className="mt">
          <span className="mt-ico">📝</span>
          {receitas.length === 0
            ? 'Nenhuma ficha cadastrada. Clique em "+ Nova ficha" para começar.'
            : 'Nenhum resultado.'}
        </div>
      ) : (
        lista.map(r => (
          <ReceitaCard key={r.id} receita={r} onEdit={abrirEdicao} onDelete={onDelete} />
        ))
      )}

      {editando && showForm && (
        <div className="ov" onClick={fecharForm}>
          <div className="mo" onClick={e => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="mo-handle" />
            <h3>✎ {editando.nome}</h3>
            <ReceitaForm
              initialData={detalhes || editando}
              insumos={insumos}
              subreceitas={subreceitas.filter(s => s.id !== editando.id)}
              produtos={produtos}
              saving={saving}
              onSubmit={async d => { await onEdit(editando.id, d); fecharForm(); }}
              onCancel={fecharForm}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Página principal com sub-tabs ──────────────────────────────────────── */
export default function ReceitasPage({ receitas: receitasProp, insumos: insumosProp, produtos, onToast, onReload, initialTarget, onTargetConsumed }) {
  const [sub,      setSub]      = useState('fichas');
  const [receitas, setReceitas] = useState(receitasProp || []);
  const [insumos,  setInsumos]  = useState(insumosProp  || []);

  useEffect(() => { if (receitasProp) setReceitas(receitasProp); }, [receitasProp]);
  useEffect(() => { if (insumosProp)  setInsumos(insumosProp);  }, [insumosProp]);

  const reloadInsumos = async () => {
    try { const d = await getInsumos(); setInsumos(d); } catch {}
  };

  return (
    <div>
      <div className="sub-tabs" style={{ marginBottom: 16 }}>
        <button className={sub === 'fichas' ? 'on' : ''} onClick={() => setSub('fichas')}>
          📝 Fichas técnicas
        </button>
        <button className={sub === 'ingredientes' ? 'on' : ''} onClick={() => setSub('ingredientes')}>
          🧂 Ingredientes
        </button>
      </div>

      {sub === 'fichas' && (
        <FichasPage
          insumos={insumos}
          setInsumos={setInsumos}
          receitas={receitas}
          setReceitas={setReceitas}
          produtos={produtos}
          onToast={onToast}
          onReload={onReload}
          initialTarget={initialTarget}
          onTargetConsumed={onTargetConsumed}
        />
      )}

      {sub === 'ingredientes' && (
        <InsumosPage
          insumos={insumos}
          onToast={onToast}
          onReload={reloadInsumos}
        />
      )}
    </div>
  );
}
